import React, { ComponentType, useEffect, useState } from 'react';
import FocusLock from 'react-focus-lock';
import { useDispatch } from 'react-redux';
import styled, { DefaultTheme, css } from 'styled-components';

import PanelWrapperStyled from './PanelWrapper.styled';
import { doPlay, getCanProceed } from './PanelWrapper.utils';

import { Heading, HeadingLevel } from '@/components/heading/Heading';
import { IconsDictionary } from '@/components/icon/Icon.types';
import IconButton from '@/components/iconButton/IconButton';
import { IconButtonTheme } from '@/components/iconButton/IconButton.types';
import PlayButton from '@/components/playButton/PlayButton';

import { CookieHelper } from '@/helpers/CookieHelper';
import { useToolbarPosition } from '@/hooks/useToolbarPosition';
import { actions } from '@/redux/slices/app';
import { VersionCardProps } from '../cards/VersionCard';

const PanelWrapper = ({ children }: React.PropsWithChildren) => {
  const position = useToolbarPosition();
  return (
    <FocusLock>
      <PanelWrapperStyled data-position={position}>
        {children}
      </PanelWrapperStyled>
    </FocusLock>
  );
};

export default PanelWrapper;

/**
 * Header
 */
interface PanelHeaderProps {
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  onBack?: () => void;
}

const PanelHeaderStyled = styled.div`
  ${() => {
    return css`
      background: var(--primary-color);
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 0.375em 0.375em 0 0;
      color: var(--color-white);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0em 1em;

      height: 4em;
      flex-shrink: 0;

      .pw-header__col {
        display: flex;
        align-items: center;
      }
      .pw-header__btn--back {
        transform: rotate(-180deg);
        margin-right: 0.5em;
      }
      .pw-header__title {
        margin: 0;
        font-size: 1em;
        line-height: 1.5em;
      }
    `;
  }}
`;

PanelWrapper.Header = (({ title, onClose, onBack }: PanelHeaderProps) => {
  const dispatch = useDispatch();
  const position = useToolbarPosition();

  const _handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (onBack) onBack();
    dispatch(actions.SET_SELECTED_BLOCK({ selectedBlock: {} }));
  };

  return (
    <PanelHeaderStyled>
      <div className="pw-header__col">
        {onBack && (
          <IconButton
            className="pw-header__btn--back"
            action={e => _handleClick(e)}
            srText="Go back"
            icon={IconsDictionary.Chevron}
            iconColor="#fff"
          />
        )}
        {title && (
          <Heading className="pw-header__title" hLevel={HeadingLevel.h1}>
            {title}
          </Heading>
        )}
      </div>
      {onClose && (
        <IconButton
          data-id="panel-btn"
          className="pw-header__btn--close"
          action={() => onClose()}
          onKeyDown={evt => {
            if (
              (position.includes('Right') && evt.key === 'ArrowRight') ||
              (position.includes('Left') && evt.key === 'ArrowLeft')
            ) {
              evt.stopPropagation();
              onClose();
            }
          }}
          srText="Close"
          icon={IconsDictionary.Close}
          iconColor="#fff"
          theme={IconButtonTheme.Tertiary}
        />
      )}
    </PanelHeaderStyled>
  );
}) as React.ComponentType<PanelHeaderProps>;

/**
 *
 * Footer
 *
 */
export interface PanelFooterProps {
  selected?: {
    blockId: string;
    defaultSelected: unknown;
    versionNo: string;
  } & VersionCardProps;
}

const PanelFooterStyled = styled.div`
  ${({ theme }: { theme: DefaultTheme }) => {
    return css`
      flex-shrink: 0;
      padding-top: 1em;
      margin: auto 0.5em 1em;
      border-top: 0.063em solid var(--color-neutral-20);
      display: flex;
      justify-content: flex-end;
      align-items: center;
      justify-content: space-between;
      background: var(--color-white);
      @media ${theme.mq.min.mobile} {
        margin: 1.5em 1em 1em;
      }
    `;
  }}
`;

PanelWrapper.Footer = (({ selected }: PanelFooterProps) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isDisabled, setDisabled] = useState(true);

  const blocksChanged = CookieHelper.GetCookie('x-blocks-changed');
  const numOfBlockChanges: number = blocksChanged
    ? JSON.parse(blocksChanged)?.length
    : 0;

  useEffect(() => getCanProceed(selected, setDisabled), [selected]);

  return (
    <PanelFooterStyled>
      <span aria-live="polite">
        {numOfBlockChanges > 1
          ? `${numOfBlockChanges} blocks changes`
          : numOfBlockChanges === 1
            ? '1 block changed'
            : 'No changes'}
      </span>
      <PlayButton
        action={() => {
          setLoading(true);
          doPlay(selected, setLoading);
        }}
        isLoading={isLoading}
        isDisabled={isDisabled}
      />
    </PanelFooterStyled>
  );
}) as ComponentType<PanelFooterProps>;
